/**
 * DM W8-1b (plan c6412 K3) — the `pm_grant` contract: a person-issued, object-bound, time-boxed authority
 * for this tenant's agents. Closed type set, per-type scope shape, TTL ceilings and use budget.
 */
import { z } from 'zod';
import { DM_ITEM_KIND_TYPE } from './dmItemKind.js';
export const DM_GRANT_TYPES = ['merge', 'command_class', 'command_once', 'sudo', 'model_override'];
/** Who may consume a grant; one value today (`chk_pm_grant_granted_to`). */
export const DM_GRANT_GRANTEES = ['agents'];
const HOUR_MS = 3_600_000;
const WAVE_MAX_MS = 14 * 24 * HOUR_MS;
/** Hard ceiling of `expires_at - now` at issue (c6413 SORU 3: wave-scoped ≤ 14 days). */
export const DM_GRANT_TTL_MAX_MS = {
    merge: 72 * HOUR_MS,
    command_once: HOUR_MS,
    command_class: WAVE_MAX_MS,
    sudo: WAVE_MAX_MS,
    model_override: WAVE_MAX_MS,
};
/** W8b-2 — a wave-scoped merge grant lives as long as any wave-scoped grant. */
export const DM_GRANT_MERGE_WAVE_TTL_MAX_MS = WAVE_MAX_MS;
/** The issue-time ceiling for this type and scope (a merge scope naming a wave is the wave form). */
export function dmGrantTtlCeilingMs(type, scope) {
    return type === 'merge' && dmMergeScopeIsWave(scope) ? DM_GRANT_MERGE_WAVE_TTL_MAX_MS : DM_GRANT_TTL_MAX_MS[type];
}
/** W8b-2 — the two merge scope forms are told apart by this key alone (stored rows and requests alike). */
export function dmMergeScopeIsWave(scope) {
    return scope.waveItemId !== undefined;
}
/** Single-use types are issued with `max_uses = 1`; a merge grant is sha-bound, so re-trying that head is the approved act. */
export const DM_GRANT_SINGLE_USE = {
    merge: false,
    command_once: true,
    command_class: false,
    sudo: false,
    model_override: false,
};
/** The work-unit statuses that end the grants scoped to its subtree (`sweepDmGrantsOnWaveTerminalInTx`). */
export const DM_GRANT_WAVE_SWEEP_STATUSES = ['done', 'abandoned', 'blocked'];
/**
 * W8d-1 (c7050 Q1=b) — the `dm.item` kinds a grant's scope item may be: a stored `waveItemId` names a WORK UNIT
 * (a wave or a standalone item); the key's name is the stored contract and stays.
 */
export const DM_GRANT_WORK_UNIT_KINDS = ['wave', 'standalone'];
/** Whether this item is a grant work unit: a `dm.item` whose kind is in {@link DM_GRANT_WORK_UNIT_KINDS}. */
export function isDmGrantWorkUnit(item) {
    return item.type === DM_ITEM_KIND_TYPE && DM_GRANT_WORK_UNIT_KINDS.includes(item.kind);
}
const idSchema = z.number().int().positive();
const sha40Schema = z.string().regex(/^[0-9a-f]{40}$/, 'a full 40-hex lower-case commit sha');
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/, 'a 64-hex sha256');
const classKeySchema = z.string().regex(/^[a-z0-9][a-z0-9_.-]{0,63}$/, 'a lowercase key (a-z 0-9 _ . -, ≤64)');
const policyKeySchema = z.string().regex(/^[a-z0-9][a-z0-9_.:-]{0,127}$/, 'a policy key');
/** `owner/name`, lower-case — the project's bound repo, recorded on a PR-wide grant for the audit trail. */
const repoSchema = z.string().max(200).regex(/^[a-z0-9_.-]+\/[a-z0-9_.-]+$/, 'owner/name');
const mergeHeadScopeSchema = z
    .object({
    jobId: idSchema.optional(),
    pr: idSchema.optional(),
    sha: sha40Schema.optional(),
    // W8-2 (c6502) — a PR number is per repo: a PR-wide grant names the project that owns the PR.
    projectId: idSchema.optional(),
    repo: repoSchema.optional(),
})
    .strict()
    .refine((s) => (s.pr === undefined) === (s.sha === undefined), 'a merge grant binds pr and sha together')
    .refine((s) => s.pr !== undefined || s.jobId !== undefined, 'a merge grant names a PR head (pr + sha) or a job')
    .refine((s) => s.jobId !== undefined || s.projectId !== undefined, 'a head merge grant without a jobId names its projectId');
/** W8b-2 (K1) — sha-less: spent only on the server's readiness read of a slice PR under the wave (`dmMergeGrantServes`). */
const mergeWaveScopeSchema = z
    .object({ waveItemId: idSchema, projectId: idSchema, repo: repoSchema.optional() })
    .strict('a wave merge grant names only waveItemId, projectId and repo — never a PR head or a job');
/** One stored shape per form; the form is picked by {@link dmMergeScopeIsWave} so each keeps its own refusal text. */
const mergeScopeSchema = z
    .record(z.unknown())
    .transform((scope, ctx) => {
    const parsed = (dmMergeScopeIsWave(scope) ? mergeWaveScopeSchema : mergeHeadScopeSchema).safeParse(scope);
    if (parsed.success)
        return parsed.data;
    for (const issue of parsed.error.issues)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: issue.path, message: issue.message });
    return z.NEVER;
});
/** The stored `pm_grant.scope` shape per type. */
export const DM_GRANT_SCOPE_SCHEMAS = {
    merge: mergeScopeSchema,
    // c6636 #6 — `projectId`: the project whose wave-less sessions may honour it (the session pack's filter).
    command_once: z
        .object({ policyKey: policyKeySchema, commandSha256: sha256Schema, waveItemId: idSchema.optional(), projectId: idSchema.optional() })
        .strict()
        // c6650 N2: one naming neither would reach no session pack (a silently dead grant).
        .refine((s) => s.waveItemId !== undefined || s.projectId !== undefined, 'a command_once grant names its waveItemId or projectId'),
    command_class: z.object({ policyKey: policyKeySchema, commandClass: classKeySchema, waveItemId: idSchema }).strict(),
    sudo: z.object({ commandClass: classKeySchema, waveItemId: idSchema }).strict(),
    model_override: z.object({ model: classKeySchema, waveItemId: idSchema }).strict(),
};
/** The scope keys a check/consume request must name and match exactly, per type (merge adds {@link dmMergeGrantServes}). */
export const DM_GRANT_MATCH_KEYS = {
    merge: ['pr', 'sha'],
    command_once: ['policyKey', 'commandSha256'],
    command_class: ['policyKey', 'commandClass', 'waveItemId'],
    sudo: ['commandClass', 'waveItemId'],
    model_override: ['model', 'waveItemId'],
};
/** W8b-2 (K6) — what a wave merge grant's coverage is matched on (`dm grant check`, a plan approval's skip). */
export const DM_GRANT_MERGE_WAVE_MATCH_KEYS = ['waveItemId', 'projectId'];
/**
 * THE merge-grant binding (c6492 B1) — consume, the job wire and the withdraw all read it: a `jobId`-scoped grant
 * serves only that job (its project follows), a `jobId`-less one any job of its PR in ITS project (W8-2 c6502: the
 * same PR number in another project's repo is another PR); a stored `pr` must be the job's; a head needs pr + sha.
 * W8b-2: a wave grant serves a head only on the server's facts — its project, the slice under the wave, all checks
 * green on exactly that head, and a review comment on the slice.
 */
export function dmMergeGrantServes(scope, probe) {
    if (dmMergeScopeIsWave(scope)) {
        const facts = probe.wave;
        return (facts !== undefined &&
            probe.sha !== undefined &&
            scope.projectId === probe.projectId &&
            facts.ready &&
            facts.reviewed &&
            facts.headSha === probe.sha &&
            facts.sliceChain.includes(scope.waveItemId));
    }
    if (scope.jobId !== undefined) {
        if (scope.jobId !== probe.jobId)
            return false;
    }
    else if (scope.pr === undefined || scope.projectId === undefined || scope.projectId !== probe.projectId) {
        return false;
    }
    if (scope.pr !== undefined && scope.pr !== probe.pr)
        return false;
    return probe.sha === undefined || (scope.pr !== undefined && scope.sha === probe.sha);
}
/** A check/consume request: the grant type plus its match keys, nothing else (merge: the spending job, when one). */
export const dmGrantRequestSchema = z.discriminatedUnion('grantType', [
    z
        .object({ grantType: z.literal('merge'), pr: idSchema, sha: sha40Schema, jobId: idSchema.optional(), projectId: idSchema.optional() })
        .strict(),
    z.object({ grantType: z.literal('command_once'), policyKey: policyKeySchema, commandSha256: sha256Schema }).strict(),
    z
        .object({ grantType: z.literal('command_class'), policyKey: policyKeySchema, commandClass: classKeySchema, waveItemId: idSchema })
        .strict(),
    z.object({ grantType: z.literal('sudo'), commandClass: classKeySchema, waveItemId: idSchema }).strict(),
    // W8-3 — `itemId` instead of the wave: a grant on any wave at or above that item serves it (the brief gate).
    z.object({ grantType: z.literal('model_override'), model: classKeySchema, waveItemId: idSchema.optional(), itemId: idSchema.optional() }).strict(),
]).superRefine((r, ctx) => {
    if (r.grantType === 'model_override' && (r.waveItemId === undefined) === (r.itemId === undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['waveItemId'], message: 'a model_override request names exactly one of waveItemId and itemId' });
    }
});
const mergeWaveCoverageSchema = z.object({ grantType: z.literal('merge'), waveItemId: idSchema, projectId: idSchema }).strict();
/**
 * W8b-2 (K6) — a check's request: every consume request, or "is there a live merge grant for this wave in this
 * project" (no head: nothing to spend, so consume refuses this form).
 */
export const dmGrantCoverageRequestSchema = z
    .record(z.unknown())
    .transform((raw, ctx) => {
    const wave = raw.grantType === 'merge' && raw.waveItemId !== undefined;
    const parsed = (wave ? mergeWaveCoverageSchema : dmGrantRequestSchema).safeParse(raw);
    if (parsed.success)
        return parsed.data;
    for (const issue of parsed.error.issues)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: issue.path, message: issue.message });
    return z.NEVER;
});
export const DM_GRANT_NOTE_MAX_LENGTH = 255;
/** The key the person's revoke door charges (pinned equal to the api constant). */
export const DM_GRANT_REVOKE_DOOR_KEY = 'pm.grant.revoke';
/**
 * `dm pr readiness` merge authority: a head merge grant (no jobId) for the head, or a wave grant whose spend conditions hold (`ok`);
 * a wave grant covering the PR while a condition is red (`gated`); one bound to a job or another head (`stale`); none;
 * a PR GitHub reports merged (`merged`) or closed without merge (`closed`): nothing left to merge, no grant owed
 * (c7176 #2, c7185 N2) — the state is {@link dmPrMergeTargetOf}'s answer whenever it is not `open`.
 */
export const DM_PR_MERGE_GRANT_STATES = ['ok', 'missing', 'stale', 'gated', 'merged', 'closed'];
/** The one predicate readiness' `mergeable` check and its merge authority both read (c7185 N2). */
export function dmPrMergeTargetOf(pr) {
    if (pr.merged)
        return 'merged';
    return pr.state === 'open' ? 'open' : 'closed';
}
//# sourceMappingURL=dmGrant.js.map